/**
 * Two-Way Subtitles Tutorial Page (Both mode, subtitles only, desktop app + KizunaAI)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app on Windows; the ones
 * not retaken for v0.44.0 are from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Bilingual Subtitles for Both Sides of a Conversation',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'This guide sets up the Sokuji desktop app to show both sides of a conversation as translated text: what you say and what the other person says, each with its translation. Use it for bilingual captions, for following a call where everyone speaks their own language, or for a translated transcript of a meeting. No synthetic voice is generated, nothing is sent into the meeting, and you need no virtual audio cable. It uses KizunaAI (Powered by Soniox; the Qwen engine works the same way). The screenshots are from Sokuji 0.44.0 on Windows with Microsoft Teams, between Chinese and English.',
  },
  steps: [
    {
      title: 'Set up Sokuji with KizunaAI',
      content:
        'If you have not done so yet, follow the <a href="/docs/kizuna-ai/quick-start">KizunaAI quick start</a> to install Sokuji and sign in.',
    },
    {
      title: 'Choose “Two-way online conversation, subtitles only”',
      content:
        'In the setup guide, choose <strong>Two-way online conversation, subtitles only</strong>. It puts Sokuji in <strong>Both</strong> mode with subtitles only. If you have already finished the setup, click <strong>Both</strong> in the mode switch at the bottom left of the main window (the first time, keep <strong>In a meeting</strong> when it asks where the other side is). Then, in the <strong>Speech</strong> block of Settings, switch off <strong>Translation the other side hears</strong> and leave <strong>Translation I hear</strong> off.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Set the two languages',
      content:
        'Set <strong>I speak</strong> to your own language and <strong>they read</strong> to the other person’s. The line underneath shows the other direction. Here: I speak Chinese, they read English.',
      screenshot: '02-languages.png',
      tip: 'Do not leave I speak on Auto Detect. In Both mode Sokuji has to know your language to translate the other side into it, and Start stays disabled until you choose one.',
    },
    {
      title: 'Choose your microphone and the meeting app',
      content:
        'In Settings, under <strong>Microphone</strong>, pick your real microphone. Under <strong>Other’s audio</strong>, pick your meeting app, here <strong>Microsoft Teams</strong>. You keep using your real microphone in the meeting app too: Sokuji only listens. In the <strong>Speech</strong> block, <strong>Translation the other side hears</strong> and <strong>Translation I hear</strong> are both off, so nothing is spoken.',
      screenshot: '03-devices.png',
      tip: 'If your meeting app is not in the list, join the call first (an app often appears only once it plays sound) and click the refresh icon, or choose System Audio (All Applications).',
    },
    {
      title: 'Start translating',
      content:
        'Click <strong>Start</strong>. Sokuji shows both sides as subtitles, each line with its translation. To let the other person read along, share the Sokuji window or open the subtitle window with <strong>Subtitle</strong> in the title bar and share that.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Save the transcript',
      content:
        'Click the download button in the toolbar. Under <strong>Include</strong>, choose whether to keep the original (<strong>Src</strong>) and the translation (<strong>Trans</strong>) for each side, then <strong>Copy to clipboard</strong>, <strong>Download as .txt</strong> or <strong>Download as .json</strong>. With <strong>Auto-save when session ends</strong> switched on, Sokuji saves it for you every time you stop. The options become available once there is something to save.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Tips for Best Results',
    items: [
      'Wear headphones. Otherwise your microphone picks up the other person from your speakers, and Sokuji shows their words as yours.',
      'Pick the meeting app rather than System Audio, so that notification sounds and music from other programs are not translated.',
      'Stop the session when the meeting ends: a running session keeps using your balance.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Does the other person see the subtitles?',
        answer: 'Only if you share your screen or the subtitle window. Otherwise the subtitles appear on your computer only.',
      },
      {
        question: 'Do I need VB-CABLE for this?',
        answer: 'No. The virtual audio cable is only needed when the other person should hear your words as a translated voice.',
      },
      {
        question: 'Can I switch to a translated voice later?',
        answer: 'Yes. Switch on Translation the other side hears and follow the two-way meeting guide to set up the virtual audio cable.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '为对话双方提供双语字幕',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content: '本指南介绍如何设置 Sokuji 桌面应用，把对话双方都显示为翻译文本：你说的话和对方说的话，各自附带翻译。可用于双语字幕、在每个人都说自己语言的通话中跟上内容，或获得会议的翻译文字记录。不会生成合成语音，不会向会议发送任何内容，也不需要虚拟音频线。本指南使用 KizunaAI（Soniox 驱动；Qwen 引擎的用法相同）。截图来自 Windows 上的 Sokuji 0.44.0 与 Microsoft Teams，对话语言为中文和英语。',
  },
  steps: [
    {
      title: '用 KizunaAI 设置 Sokuji',
      content: '如果还没有完成，请按照 <a href="/docs/kizuna-ai/quick-start">KizunaAI 快速入门</a>安装 Sokuji 并登录。',
    },
    {
      title: '选择“在线双向对话，只要字幕”',
      content: '在设置向导中选择<strong>在线双向对话，只要字幕</strong>。它会让 Sokuji 进入<strong>两者</strong>模式，并且只显示字幕。如果已经完成设置，请点击主窗口左下角模式切换中的<strong>两者</strong>（第一次点击时会询问对方在哪里，保持<strong>在线会议</strong>即可），然后在设置的<strong>语音</strong>部分关闭<strong>对方听到的翻译</strong>，并让<strong>我听到的翻译</strong>保持关闭。',
      screenshot: '01-scenario.png',
    },
    {
      title: '设置两种语言',
      content: '将<strong>我说</strong>设为你自己的语言，将<strong>对方读到</strong>设为对方的语言。下方那一行会显示另一个方向。此处：我说中文，对方读到英语。',
      screenshot: '02-languages.png',
      tip: '不要让“我说”停留在“自动检测”。在两者模式下，Sokuji 需要知道你的语言才能把对方的话翻译过来；在你选定语言之前，“开始”按钮会一直不可用。',
    },
    {
      title: '选择麦克风和会议应用',
      content: '在设置的<strong>麦克风</strong>下选择你的真实麦克风。在<strong>对方音频</strong>下选择你的会议应用，此处为 <strong>Microsoft Teams</strong>。在会议应用中你也继续使用真实麦克风：Sokuji 只负责收听。在<strong>语音</strong>部分，<strong>对方听到的翻译</strong>和<strong>我听到的翻译</strong>都处于关闭状态，因此不会朗读任何内容。',
      screenshot: '03-devices.png',
      tip: '如果列表中没有你的会议应用，请先加入通话（应用通常要在播放声音后才会出现），然后点击刷新图标，或选择 System Audio (All Applications)。',
    },
    {
      title: '开始翻译',
      content: '点击<strong>开始</strong>。Sokuji 会以字幕显示双方的内容，每一行都附带翻译。如果想让对方也能阅读，可以共享 Sokuji 窗口，或者点击标题栏中的<strong>字幕</strong>打开字幕窗口并共享它。',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '保存文字记录',
      content: '点击工具栏中的下载按钮。在<strong>包含</strong>下，为每一方选择是否保留原文（<strong>原文</strong>）和译文（<strong>译文</strong>），然后选择<strong>复制到剪贴板</strong>、<strong>下载为 .txt</strong> 或<strong>下载为 .json</strong>。打开<strong>会话结束时自动保存</strong>后，每次停止时 Sokuji 都会自动为你保存。有内容可保存时，这些选项才会可用。',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: '最佳使用建议',
    items: [
      '请戴耳机。否则你的麦克风会拾取扬声器里对方的声音，Sokuji 会把他们的话显示成你的话。',
      '选择会议应用而不是 System Audio，这样其他程序的通知音和音乐就不会被翻译。',
      '会议结束后请停止会话：正在运行的会话会持续消耗你的余额。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '对方能看到字幕吗？',
        answer: '只有在你共享屏幕或字幕窗口时才能看到。否则字幕只显示在你的电脑上。',
      },
      {
        question: '这需要 VB-CABLE 吗？',
        answer: '不需要。只有在需要让对方以翻译语音听到你的话时，才需要虚拟音频线。',
      },
      {
        question: '以后可以改用翻译语音吗？',
        answer: '可以。打开“对方听到的翻译”，并按照双向会议指南设置虚拟音频线。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content: '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '会話の双方にバイリンガル字幕を表示する',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content: 'このガイドでは、会話の双方を翻訳テキストで表示するように Sokuji デスクトップアプリを設定します。あなたの発言も相手の発言も、それぞれ翻訳付きで表示されます。バイリンガル字幕として、全員が自分の言語で話す通話についていくために、あるいは会議の翻訳付き記録を残すために使えます。合成音声は生成されず、会議には何も送られず、仮想オーディオケーブルも不要です。KizunaAI（Soniox 提供。Qwen エンジンでも同じ手順です）を使用します。スクリーンショットは Windows 版 Sokuji 0.44.0 と Microsoft Teams のもので、中国語と英語の会話です。',
  },
  steps: [
    {
      title: 'KizunaAI で Sokuji をセットアップする',
      content: 'まだの場合は、<a href="/docs/kizuna-ai/quick-start">KizunaAI クイックスタート</a>に沿って Sokuji をインストールし、サインインしてください。',
    },
    {
      title: '「オンラインで双方向に会話（字幕のみ）」を選ぶ',
      content: 'セットアップガイドで<strong>オンラインで双方向に会話（字幕のみ）</strong>を選びます。Sokuji が字幕のみの<strong>両方</strong>モードになります。すでにセットアップを終えている場合は、メイン画面左下のモード切り替えで<strong>両方</strong>をクリックします（初回に相手がどこにいるかを尋ねられたら、<strong>オンライン会議</strong>のままにします）。次に、設定の<strong>音声</strong>セクションで<strong>相手に聞こえる翻訳</strong>をオフにし、<strong>自分に聞こえる翻訳</strong>もオフのままにします。',
      screenshot: '01-scenario.png',
    },
    {
      title: '2 つの言語を設定する',
      content: '<strong>話す言語</strong>を自分の言語に、<strong>相手が読む言語</strong>を相手の言語に設定します。その下の行に逆方向が表示されます。ここでは、話す言語は中国語、相手が読む言語は英語です。',
      screenshot: '02-languages.png',
      tip: '「話す言語」を「自動検出」のままにしないでください。両方モードでは、相手の発言をあなたの言語に翻訳するために Sokuji があなたの言語を知る必要があり、言語を選ぶまで「開始」は押せません。',
    },
    {
      title: 'マイクと会議アプリを選ぶ',
      content: '設定の<strong>マイクロフォン</strong>で実際のマイクを選びます。<strong>相手の音声</strong>で会議アプリ（ここでは <strong>Microsoft Teams</strong>）を選びます。会議アプリでも引き続き実際のマイクを使います。Sokuji は聞くだけです。<strong>音声</strong>セクションでは<strong>相手に聞こえる翻訳</strong>と<strong>自分に聞こえる翻訳</strong>がどちらもオフなので、何も読み上げられません。',
      screenshot: '03-devices.png',
      tip: '会議アプリが一覧にない場合は、先に通話に参加してから（アプリは音を再生して初めて表示されることがよくあります）更新アイコンをクリックするか、System Audio (All Applications) を選んでください。',
    },
    {
      title: '翻訳を開始する',
      content: '<strong>開始</strong>をクリックします。Sokuji は双方の発言を、各行に翻訳を添えて字幕で表示します。相手にも読んでもらうには、Sokuji のウィンドウを共有するか、タイトルバーの<strong>字幕</strong>で字幕ウィンドウを開いてそれを共有します。',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '記録を保存する',
      content: 'ツールバーのダウンロードボタンをクリックします。<strong>含める</strong>で、それぞれの側について原文（<strong>原文</strong>）と訳文（<strong>訳文</strong>）を残すかを選び、<strong>クリップボードにコピー</strong>、<strong>.txt でダウンロード</strong>、<strong>.json でダウンロード</strong>のいずれかを選びます。<strong>セッション終了時に自動保存</strong>をオンにすると、停止するたびに Sokuji が自動で保存します。これらの項目は、保存する内容ができてから使えるようになります。',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: '最良の結果を得るためのヒント',
    items: [
      'ヘッドホンを着けてください。着けないと、スピーカーから流れる相手の声をマイクが拾い、Sokuji が相手の発言をあなたの発言として表示してしまいます。',
      'System Audio ではなく会議アプリを選ぶと、ほかのプログラムの通知音や音楽が翻訳されません。',
      '会議が終わったらセッションを停止してください。実行中のセッションは残高を消費し続けます。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: '相手にも字幕が見えますか？',
        answer: '画面または字幕ウィンドウを共有した場合だけです。それ以外の場合、字幕はあなたのコンピューターにだけ表示されます。',
      },
      {
        question: 'VB-CABLE は必要ですか？',
        answer: 'いいえ。仮想オーディオケーブルが必要なのは、あなたの発言を翻訳音声として相手に聞かせるときだけです。',
      },
      {
        question: 'あとから翻訳音声に切り替えられますか？',
        answer: 'はい。「相手に聞こえる翻訳」をオンにし、双方向の会議ガイドに沿って仮想オーディオケーブルを設定してください。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content: 'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '대화 양쪽을 위한 이중 언어 자막',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content: '이 가이드는 대화의 양쪽, 즉 내가 한 말과 상대방이 한 말을 각각 번역과 함께 텍스트로 보여 주도록 Sokuji 데스크톱 앱을 설정합니다. 이중 언어 자막, 모두가 자기 언어로 말하는 통화 따라가기, 회의의 번역된 대화 기록 등에 쓸 수 있습니다. 합성 음성은 생성되지 않고, 회의에는 아무것도 전송되지 않으며, 가상 오디오 케이블도 필요 없습니다. KizunaAI(Soniox 제공이며, Qwen 엔진도 같은 방식으로 작동합니다)를 사용합니다. 스크린샷은 Windows용 Sokuji 0.44.0과 Microsoft Teams에서 촬영했으며, 중국어와 영어로 대화하는 예입니다.',
  },
  steps: [
    {
      title: 'KizunaAI로 Sokuji 설정',
      content: '아직 하지 않았다면 <a href="/docs/kizuna-ai/quick-start">KizunaAI 빠른 시작</a>을 따라 Sokuji를 설치하고 로그인하세요.',
    },
    {
      title: '“양방향 온라인 대화, 자막만” 선택',
      content: '설정 가이드에서 <strong>양방향 온라인 대화, 자막만</strong>을 선택하세요. Sokuji가 자막만 표시하는 <strong>모두</strong> 모드로 설정됩니다. 이미 설정을 마쳤다면 메인 창 왼쪽 아래의 모드 전환에서 <strong>모두</strong>를 클릭하세요(처음에 상대방이 어디에 있는지 물으면 <strong>온라인 회의</strong>를 그대로 두세요). 그런 다음 설정의 <strong>음성</strong> 섹션에서 <strong>상대방에게 들리는 번역</strong>을 끄고, <strong>내가 듣는 번역</strong>은 꺼 둔 채로 두세요.',
      screenshot: '01-scenario.png',
    },
    {
      title: '두 언어 설정',
      content: '<strong>내가 말하는 언어</strong>를 내 언어로, <strong>상대방이 읽는 언어</strong>를 상대방의 언어로 설정하세요. 아래 줄에 반대 방향이 표시됩니다. 여기서는 내가 말하는 언어는 중국어, 상대방이 읽는 언어는 영어입니다.',
      screenshot: '02-languages.png',
      tip: '“내가 말하는 언어”를 “자동 감지”로 두지 마세요. 모두 모드에서는 상대방의 말을 내 언어로 번역하려면 Sokuji가 내 언어를 알아야 하므로, 언어를 고를 때까지 “시작”이 비활성화됩니다.',
    },
    {
      title: '마이크와 회의 앱 선택',
      content: '설정의 <strong>마이크</strong>에서 실제 마이크를 고르세요. <strong>상대방 오디오</strong>에는 회의 앱(여기서는 <strong>Microsoft Teams</strong>)을 고르세요. 회의 앱에서도 계속 실제 마이크를 사용합니다. Sokuji는 듣기만 합니다. <strong>음성</strong> 섹션에서는 <strong>상대방에게 들리는 번역</strong>과 <strong>내가 듣는 번역</strong>이 모두 꺼져 있으므로 아무것도 음성으로 나오지 않습니다.',
      screenshot: '03-devices.png',
      tip: '회의 앱이 목록에 없다면 먼저 통화에 참여한 다음(앱은 소리를 재생해야 나타나는 경우가 많습니다) 새로고침 아이콘을 클릭하거나, System Audio (All Applications)를 선택하세요.',
    },
    {
      title: '번역 시작',
      content: '<strong>시작</strong>을 클릭하세요. Sokuji가 양쪽의 말을 줄마다 번역을 붙여 자막으로 보여 줍니다. 상대방도 함께 읽게 하려면 Sokuji 창을 공유하거나, 제목 표시줄의 <strong>자막</strong>으로 자막 창을 열어 그 창을 공유하세요.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '대화 기록 저장',
      content: '도구 모음의 다운로드 버튼을 클릭하세요. <strong>포함</strong>에서 양쪽 각각의 원문(<strong>원문</strong>)과 번역(<strong>번역</strong>)을 남길지 고른 다음 <strong>클립보드에 복사</strong>, <strong>.txt로 다운로드</strong> 또는 <strong>.json으로 다운로드</strong>를 선택하세요. <strong>세션 종료 시 자동 저장</strong>을 켜 두면 세션을 중지할 때마다 Sokuji가 알아서 저장합니다. 이 옵션들은 저장할 내용이 생기면 사용할 수 있습니다.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: '최상의 결과를 위한 팁',
    items: [
      '헤드폰을 착용하세요. 그렇지 않으면 마이크가 스피커에서 나오는 상대방의 목소리를 받아, Sokuji가 상대방의 말을 내 말로 표시합니다.',
      'System Audio 대신 회의 앱을 고르면 다른 프로그램의 알림음이나 음악이 번역되지 않습니다.',
      '회의가 끝나면 세션을 중지하세요. 실행 중인 세션은 계속 잔액을 사용합니다.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '상대방도 자막을 볼 수 있나요?',
        answer: '화면이나 자막 창을 공유할 때만 볼 수 있습니다. 그렇지 않으면 자막은 내 컴퓨터에만 표시됩니다.',
      },
      {
        question: 'VB-CABLE이 필요한가요?',
        answer: '아니요. 가상 오디오 케이블은 상대방이 내 말을 번역된 음성으로 들어야 할 때만 필요합니다.',
      },
      {
        question: '나중에 번역 음성으로 바꿀 수 있나요?',
        answer: '네. “상대방에게 들리는 번역”을 켜고 양방향 회의 가이드를 따라 가상 오디오 케이블을 설정하세요.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content: '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Subtítulos bilingües para ambos lados de una conversación',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content: 'Esta guía configura la aplicación de escritorio de Sokuji para mostrar ambos lados de una conversación como texto traducido: lo que dices tú y lo que dice la otra persona, cada uno con su traducción. Úsala para subtítulos bilingües, para seguir una llamada en la que cada uno habla su propio idioma o para obtener una transcripción traducida de una reunión. No se genera ninguna voz sintética, no se envía nada a la reunión y no necesitas un cable de audio virtual. Usa KizunaAI (Con tecnología de Soniox; el motor de Qwen funciona igual). Las capturas son de Sokuji 0.44.0 en Windows con Microsoft Teams, entre chino e inglés.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content: 'Si aún no lo has hecho, sigue el <a href="/docs/kizuna-ai/quick-start">inicio rápido de KizunaAI</a> para instalar Sokuji e iniciar sesión.',
    },
    {
      title: 'Elige «Conversación en línea bidireccional, solo subtítulos»',
      content: 'En la guía de configuración, elige <strong>Conversación en línea bidireccional, solo subtítulos</strong>. Pone Sokuji en modo <strong>Ambos</strong>, solo con subtítulos. Si ya terminaste la configuración, haz clic en <strong>Ambos</strong> en el selector de modo, abajo a la izquierda de la ventana principal (la primera vez, deja <strong>En una reunión</strong> cuando te pregunte dónde está la otra parte). Después, en la sección <strong>Voz</strong> de Configuración, desactiva <strong>Traducción que oye la otra parte</strong> y deja desactivada <strong>Traducción que yo oigo</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Configura los dos idiomas',
      content: 'Pon en <strong>Yo hablo</strong> tu propio idioma y en <strong>ellos leen</strong> el de la otra persona. La línea de debajo muestra la otra dirección. Aquí: Yo hablo Chino, ellos leen Inglés.',
      screenshot: '02-languages.png',
      tip: 'No dejes Yo hablo en Detección Automática. En el modo Ambos, Sokuji necesita saber tu idioma para traducir a él lo que dice la otra parte, e Iniciar sigue desactivado hasta que elijas uno.',
    },
    {
      title: 'Elige tu micrófono y la aplicación de reuniones',
      content: 'En Configuración, en <strong>Micrófono</strong>, elige tu micrófono real. En <strong>Audio del otro</strong>, elige tu aplicación de reuniones, aquí <strong>Microsoft Teams</strong>. En la aplicación de reuniones sigues usando también tu micrófono real: Sokuji solo escucha. En la sección <strong>Voz</strong>, <strong>Traducción que oye la otra parte</strong> y <strong>Traducción que yo oigo</strong> están desactivadas, así que no se pronuncia nada.',
      screenshot: '03-devices.png',
      tip: 'Si tu aplicación de reuniones no aparece en la lista, únete primero a la llamada (a menudo una aplicación solo aparece cuando reproduce sonido) y haz clic en el icono de actualizar, o elige System Audio (All Applications).',
    },
    {
      title: 'Empieza a traducir',
      content: 'Haz clic en <strong>Iniciar</strong>. Sokuji muestra ambos lados como subtítulos, cada línea con su traducción. Para que la otra persona también pueda leerlos, comparte la ventana de Sokuji, o abre la ventana de subtítulos con <strong>Subtítulos</strong> en la barra de título y compártela.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Guarda la transcripción',
      content: 'Haz clic en el botón de descarga de la barra de herramientas. En <strong>Incluir</strong>, elige si conservar el original (<strong>Origen</strong>) y la traducción (<strong>Traducción</strong>) de cada lado, y luego <strong>Copiar al portapapeles</strong>, <strong>Descargar como .txt</strong> o <strong>Descargar como .json</strong>. Con <strong>Guardar automáticamente al terminar la sesión</strong> activado, Sokuji la guarda por ti cada vez que te detienes. Las opciones se habilitan cuando hay algo que guardar.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Consejos para obtener mejores resultados',
    items: [
      'Usa auriculares. Si no, tu micrófono capta a la otra persona desde tus altavoces, y Sokuji muestra sus palabras como si fueran tuyas.',
      'Elige la aplicación de reuniones en lugar de System Audio, para que no se traduzcan los sonidos de notificación ni la música de otros programas.',
      'Detén la sesión cuando termine la reunión: una sesión en marcha sigue consumiendo tu saldo.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿La otra persona ve los subtítulos?',
        answer: 'Solo si compartes tu pantalla o la ventana de subtítulos. Si no, los subtítulos solo aparecen en tu ordenador.',
      },
      {
        question: '¿Necesito VB-CABLE para esto?',
        answer: 'No. El cable de audio virtual solo hace falta cuando la otra persona debe oír tus palabras como voz traducida.',
      },
      {
        question: '¿Puedo cambiar después a una voz traducida?',
        answer: 'Sí. Activa Traducción que oye la otra parte y sigue la guía de reuniones bidireccionales para configurar el cable de audio virtual.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content: '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Sous-titres bilingues pour les deux côtés d’une conversation',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content: 'Ce guide configure l’application de bureau Sokuji pour afficher les deux côtés d’une conversation sous forme de texte traduit : ce que vous dites et ce que dit votre interlocuteur, chacun avec sa traduction. Utilisez-le pour des sous-titres bilingues, pour suivre un appel où chacun parle sa propre langue, ou pour obtenir une transcription traduite d’une réunion. Aucune voix de synthèse n’est générée, rien n’est envoyé dans la réunion, et vous n’avez besoin d’aucun câble audio virtuel. Il utilise KizunaAI (Propulsé par Soniox ; le moteur Qwen fonctionne de la même façon). Les captures d’écran proviennent de Sokuji 0.44.0 sous Windows avec Microsoft Teams, entre le chinois et l’anglais.',
  },
  steps: [
    {
      title: 'Configurez Sokuji avec KizunaAI',
      content: 'Si ce n’est pas encore fait, suivez le <a href="/docs/kizuna-ai/quick-start">démarrage rapide KizunaAI</a> pour installer Sokuji et vous connecter.',
    },
    {
      title: 'Choisissez « Conversation en ligne bidirectionnelle, sous-titres uniquement »',
      content: 'Dans le guide de configuration, choisissez <strong>Conversation en ligne bidirectionnelle, sous-titres uniquement</strong>. Sokuji passe alors en mode <strong>Les deux</strong>, avec des sous-titres uniquement. Si vous avez déjà terminé la configuration, cliquez sur <strong>Les deux</strong> dans le sélecteur de mode, en bas à gauche de la fenêtre principale (la première fois, gardez <strong>En réunion</strong> quand Sokuji vous demande où est l’autre partie). Ensuite, dans la section <strong>Voix</strong> des Paramètres, désactivez <strong>Traduction entendue par l\'autre</strong> et laissez <strong>Traduction que j\'entends</strong> désactivée.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Réglez les deux langues',
      content: 'Réglez <strong>je parle</strong> sur votre propre langue et <strong>ils lisent</strong> sur celle de votre interlocuteur. La ligne en dessous indique l’autre sens. Ici : je parle chinois, ils lisent anglais.',
      screenshot: '02-languages.png',
      tip: 'Ne laissez pas je parle sur Détection Automatique. En mode Les deux, Sokuji doit connaître votre langue pour y traduire les paroles de l’autre partie, et Démarrer reste désactivé tant que vous n’en avez pas choisi une.',
    },
    {
      title: 'Choisissez votre microphone et l’application de réunion',
      content: 'Dans les Paramètres, sous <strong>Microphone</strong>, sélectionnez votre vrai microphone. Sous <strong>Audio de l\'autre</strong>, sélectionnez votre application de réunion, ici <strong>Microsoft Teams</strong>. Vous continuez aussi d’utiliser votre vrai microphone dans l’application de réunion : Sokuji ne fait qu’écouter. Dans la section <strong>Voix</strong>, <strong>Traduction entendue par l\'autre</strong> et <strong>Traduction que j\'entends</strong> sont toutes deux désactivées : rien n’est prononcé.',
      screenshot: '03-devices.png',
      tip: 'Si votre application de réunion n’apparaît pas dans la liste, rejoignez d’abord l’appel (une application n’apparaît souvent qu’une fois qu’elle émet du son) et cliquez sur l’icône d’actualisation, ou choisissez System Audio (All Applications).',
    },
    {
      title: 'Lancez la traduction',
      content: 'Cliquez sur <strong>Démarrer</strong>. Sokuji affiche les deux côtés en sous-titres, chaque ligne avec sa traduction. Pour que votre interlocuteur puisse lire aussi, partagez la fenêtre de Sokuji, ou ouvrez la fenêtre de sous-titres avec <strong>Sous-titres</strong> dans la barre de titre et partagez-la.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Enregistrez la transcription',
      content: 'Cliquez sur le bouton de téléchargement de la barre d’outils. Sous <strong>Inclure</strong>, choisissez de conserver ou non l’original (<strong>Source</strong>) et la traduction (<strong>Traduction</strong>) de chaque côté, puis <strong>Copier dans le presse-papiers</strong>, <strong>Télécharger en .txt</strong> ou <strong>Télécharger en .json</strong>. Avec <strong>Enregistrer automatiquement en fin de session</strong> activé, Sokuji l’enregistre pour vous à chaque arrêt. Les options deviennent disponibles dès qu’il y a quelque chose à enregistrer.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Conseils pour de meilleurs résultats',
    items: [
      'Portez un casque. Sinon, votre microphone capte votre interlocuteur depuis vos haut-parleurs, et Sokuji affiche ses paroles comme si c’étaient les vôtres.',
      'Choisissez l’application de réunion plutôt que System Audio, pour que les sons de notification et la musique des autres programmes ne soient pas traduits.',
      'Arrêtez la session à la fin de la réunion : une session en cours continue de consommer votre solde.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Mon interlocuteur voit-il les sous-titres ?',
        answer: 'Seulement si vous partagez votre écran ou la fenêtre de sous-titres. Sinon, les sous-titres ne s’affichent que sur votre ordinateur.',
      },
      {
        question: 'Ai-je besoin de VB-CABLE pour cela ?',
        answer: 'Non. Le câble audio virtuel n’est nécessaire que lorsque votre interlocuteur doit entendre vos paroles sous forme de voix traduite.',
      },
      {
        question: 'Puis-je passer plus tard à une voix traduite ?',
        answer: 'Oui. Activez « Traduction entendue par l\'autre » et suivez le guide des réunions bidirectionnelles pour configurer le câble audio virtuel.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content: 'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'ترجمة نصية ثنائية اللغة لطرفَي المحادثة',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content: 'يُعدّ هذا الدليل تطبيق Sokuji لسطح المكتب لعرض طرفَي المحادثة كنص مترجَم: ما تقوله أنت وما يقوله الطرف الآخر، كلٌّ مع ترجمته. استخدمه لترجمة نصية ثنائية اللغة، أو لمتابعة مكالمة يتحدث فيها كل شخص بلغته، أو للحصول على نص مكتوب مترجَم لاجتماع. لا يُولَّد أي صوت اصطناعي، ولا يُرسَل أي شيء إلى الاجتماع، ولا تحتاج إلى كابل صوت افتراضي. يستخدم الدليل KizunaAI (مدعوم من Soniox؛ ويعمل محرك Qwen بالطريقة نفسها). لقطات الشاشة مأخوذة من Sokuji 0.44.0 على Windows مع Microsoft Teams، بين الصينية والإنجليزية.',
  },
  steps: [
    {
      title: 'اضبط Sokuji مع KizunaAI',
      content: 'إذا لم تفعل ذلك بعد، فاتبع <a href="/docs/kizuna-ai/quick-start">البدء السريع مع KizunaAI</a> لتثبيت Sokuji وتسجيل الدخول.',
    },
    {
      title: 'اختر «محادثة عبر الإنترنت بالاتجاهين، نص فقط»',
      content: 'في دليل الإعداد، اختر <strong>محادثة عبر الإنترنت بالاتجاهين، نص فقط</strong>. يضع ذلك Sokuji في وضع <strong>الكل</strong> مع ترجمة نصية فقط. إذا كنت قد أنهيت الإعداد بالفعل، فانقر على <strong>الكل</strong> في مبدّل الأوضاع أسفل يسار النافذة الرئيسية (في المرة الأولى، أبقِ الاختيار على <strong>في اجتماع</strong> عندما يسألك عن مكان الطرف الآخر). ثم، في قسم <strong>الكلام</strong> في الإعدادات، أوقف <strong>الترجمة التي يسمعها الطرف الآخر</strong> واترك <strong>الترجمة التي أسمعها</strong> متوقفة.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'اضبط اللغتين',
      content: 'اضبط <strong>أتحدث</strong> على لغتك و<strong>يقرأون</strong> على لغة الطرف الآخر. يعرض السطر أسفلهما الاتجاه الآخر. هنا: أتحدث الصينية، يقرأون الإنجليزية.',
      screenshot: '02-languages.png',
      tip: 'لا تترك «أتحدث» على «اكتشاف تلقائي». في وضع الكل يجب أن يعرف Sokuji لغتك ليترجم كلام الطرف الآخر إليها، ويبقى زر «ابدأ» معطّلًا حتى تختار لغة.',
    },
    {
      title: 'اختر الميكروفون وتطبيق الاجتماع',
      content: 'في الإعدادات، تحت <strong>ميكروفون</strong>، اختر ميكروفونك الحقيقي. وتحت <strong>صوت الآخر</strong>، اختر تطبيق الاجتماع، وهو هنا <strong>Microsoft Teams</strong>. وتواصل أيضًا استخدام ميكروفونك الحقيقي في تطبيق الاجتماع: فـ Sokuji يستمع فقط. في قسم <strong>الكلام</strong>، يكون خيارا <strong>الترجمة التي يسمعها الطرف الآخر</strong> و<strong>الترجمة التي أسمعها</strong> متوقفين كلاهما، فلا يُنطق أي شيء.',
      screenshot: '03-devices.png',
      tip: 'إذا لم يظهر تطبيق الاجتماع في القائمة، فانضم إلى المكالمة أولًا (غالبًا لا يظهر التطبيق إلا بعد أن يشغّل صوتًا) وانقر على أيقونة التحديث، أو اختر System Audio (All Applications).',
    },
    {
      title: 'ابدأ الترجمة',
      content: 'انقر على <strong>ابدأ</strong>. يعرض Sokuji كلام الطرفين كترجمة نصية، وكل سطر مع ترجمته. لكي يقرأ الطرف الآخر معك، شارك نافذة Sokuji، أو افتح نافذة الترجمة النصية عبر <strong>ترجمة</strong> في شريط العنوان وشاركها.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'احفظ النص المكتوب',
      content: 'انقر على زر التنزيل في شريط الأدوات. تحت <strong>تضمين</strong>، اختر ما إذا كنت تريد الاحتفاظ بالأصل (<strong>الأصل</strong>) والترجمة (<strong>الترجمة</strong>) لكل طرف، ثم <strong>نسخ إلى الحافظة</strong> أو <strong>تنزيل بصيغة .txt</strong> أو <strong>تنزيل بصيغة .json</strong>. عند تفعيل <strong>حفظ تلقائي عند انتهاء الجلسة</strong>، يحفظه Sokuji نيابةً عنك في كل مرة تتوقف فيها. تصبح الخيارات متاحة عندما يكون هناك ما يُحفَظ.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'نصائح لأفضل النتائج',
    items: [
      'ارتدِ سماعات الرأس. وإلا فسيلتقط ميكروفونك صوت الطرف الآخر من مكبرات الصوت، وسيعرض Sokuji كلامه على أنه كلامك.',
      'اختر تطبيق الاجتماع بدلًا من System Audio، حتى لا تُترجَم أصوات الإشعارات والموسيقى الصادرة عن البرامج الأخرى.',
      'أوقف الجلسة عند انتهاء الاجتماع: فالجلسة الجارية تستمر في استهلاك رصيدك.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'هل يرى الطرف الآخر الترجمة النصية؟',
        answer: 'فقط إذا شاركت شاشتك أو نافذة الترجمة النصية. وإلا فلا تظهر الترجمة النصية إلا على حاسوبك.',
      },
      {
        question: 'هل أحتاج إلى VB-CABLE لهذا؟',
        answer: 'لا. لا تحتاج إلى كابل الصوت الافتراضي إلا عندما يجب أن يسمع الطرف الآخر كلامك بصوت مترجَم.',
      },
      {
        question: 'هل يمكنني التحول إلى صوت مترجَم لاحقًا؟',
        answer: 'نعم. فعّل «الترجمة التي يسمعها الطرف الآخر» واتبع دليل الاجتماعات ثنائية الاتجاه لإعداد كابل الصوت الافتراضي.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content: 'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Legendas bilíngues para os dois lados de uma conversa',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Este guia configura o aplicativo de desktop do Sokuji para mostrar os dois lados de uma conversa como texto traduzido: o que você diz e o que a outra pessoa diz, cada um com sua tradução. Use-o para legendas bilíngues, para acompanhar uma chamada em que cada um fala o próprio idioma ou para ter uma transcrição traduzida de uma reunião. Nenhuma voz sintética é gerada, nada é enviado para a reunião, e você não precisa de cabo de áudio virtual. Ele usa a KizunaAI (Com tecnologia Soniox; o mesmo vale para o mecanismo Qwen). As capturas de tela são do Sokuji 0.44.0 no Windows com o Microsoft Teams, entre chinês e inglês.',
  },
  steps: [
    {
      title: 'Configure o Sokuji com a KizunaAI',
      content:
        'Se ainda não fez isso, siga o <a href="/docs/kizuna-ai/quick-start">início rápido com KizunaAI</a> para instalar o Sokuji e entrar na sua conta.',
    },
    {
      title: 'Escolha “Conversa on-line em dois sentidos, somente legendas”',
      content:
        'No guia de configuração, escolha <strong>Conversa on-line em dois sentidos, somente legendas</strong>. Isso coloca o Sokuji no modo <strong>Ambos</strong>, só com legendas. Se você já concluiu a configuração, clique em <strong>Ambos</strong> no seletor de modo, no canto inferior esquerdo da janela principal (na primeira vez, mantenha <strong>Em uma reunião</strong> quando o painel perguntar onde está a outra pessoa). Depois, na seção <strong>Voz</strong> das Configurações, desative <strong>Tradução que o outro lado ouve</strong> e deixe <strong>Tradução que eu ouço</strong> desativada.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Defina os dois idiomas',
      content:
        'Defina <strong>eu falo</strong> como o seu idioma e <strong>eles leem</strong> como o da outra pessoa. A linha abaixo mostra a outra direção. Aqui: eu falo Chinês, eles leem Inglês.',
      screenshot: '02-languages.png',
      tip: 'Não deixe eu falo em Detecção Automática. No modo Ambos, o Sokuji precisa saber o seu idioma para traduzir a outra pessoa para ele, e Iniciar continua desativado até você escolher um.',
    },
    {
      title: 'Escolha seu microfone e o aplicativo de reunião',
      content:
        'Em Configurações, em <strong>Microfone</strong>, escolha seu microfone real. Em <strong>Áudio do outro</strong>, escolha seu aplicativo de reunião, aqui o <strong>Microsoft Teams</strong>. Você continua usando seu microfone real também no aplicativo de reunião: o Sokuji apenas escuta. Na seção <strong>Voz</strong>, <strong>Tradução que o outro lado ouve</strong> e <strong>Tradução que eu ouço</strong> estão ambas desativadas, então nada é falado.',
      screenshot: '03-devices.png',
      tip: 'Se o seu aplicativo de reunião não estiver na lista, entre primeiro na chamada (muitas vezes um aplicativo só aparece quando reproduz som) e clique no ícone de atualizar, ou escolha System Audio (All Applications).',
    },
    {
      title: 'Comece a traduzir',
      content:
        'Clique em <strong>Iniciar</strong>. O Sokuji mostra os dois lados como legendas, cada linha com sua tradução. Para que a outra pessoa também possa ler, compartilhe a janela do Sokuji ou abra a janela de legendas com <strong>Legendas</strong> na barra de título e compartilhe essa janela.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Salve a transcrição',
      content:
        'Clique no botão de download na barra de ferramentas. Em <strong>Incluir</strong>, escolha se quer manter o original (<strong>Original</strong>) e a tradução (<strong>Tradução</strong>) de cada lado e depois clique em <strong>Copiar para área de transferência</strong>, <strong>Baixar como .txt</strong> ou <strong>Baixar como .json</strong>. Com <strong>Salvar automaticamente ao encerrar a sessão</strong> ativado, o Sokuji salva a transcrição para você sempre que você para. As opções ficam disponíveis quando há algo para salvar.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Dicas para melhores resultados',
    items: [
      'Use fones de ouvido. Caso contrário, seu microfone capta a outra pessoa pelos seus alto-falantes, e o Sokuji mostra as palavras dela como se fossem suas.',
      'Escolha o aplicativo de reunião em vez de System Audio, para que sons de notificação e músicas de outros programas não sejam traduzidos.',
      'Encerre a sessão quando a reunião terminar: uma sessão em andamento continua consumindo seu saldo.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'A outra pessoa vê as legendas?',
        answer: 'Só se você compartilhar sua tela ou a janela de legendas. Caso contrário, as legendas aparecem apenas no seu computador.',
      },
      {
        question: 'Preciso do VB-CABLE para isso?',
        answer: 'Não. O cabo de áudio virtual só é necessário quando a outra pessoa precisa ouvir suas palavras como uma voz traduzida.',
      },
      {
        question: 'Posso mudar para uma voz traduzida depois?',
        answer: 'Sim. Ative Tradução que o outro lado ouve e siga o guia de reuniões bidirecionais para configurar o cabo de áudio virtual.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Двуязычные субтитры для обеих сторон разговора',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Это руководство настраивает настольное приложение Sokuji так, чтобы обе стороны разговора отображались в виде переведённого текста: ваши слова и слова собеседника, каждые со своим переводом. Используйте это для двуязычных субтитров, чтобы следить за звонком, где каждый говорит на своём языке, или чтобы получить переведённую расшифровку встречи. Синтезированный голос не создаётся, во встречу ничего не отправляется, и виртуальный аудиокабель не нужен. Используется KizunaAI (на базе Soniox; движок Qwen работает так же). Снимки экрана сделаны в Sokuji 0.44.0 на Windows с Microsoft Teams, между китайским и английским.',
  },
  steps: [
    {
      title: 'Настройте Sokuji с KizunaAI',
      content:
        'Если вы ещё этого не сделали, следуйте руководству <a href="/docs/kizuna-ai/quick-start">«Быстрый старт с KizunaAI»</a>, чтобы установить Sokuji и войти в учётную запись.',
    },
    {
      title: 'Выберите «Двусторонний онлайн-разговор, только субтитры»',
      content:
        'В мастере настройки выберите <strong>Двусторонний онлайн-разговор, только субтитры</strong>. Sokuji переходит в режим <strong>Оба</strong> только с субтитрами. Если вы уже завершили настройку, нажмите <strong>Оба</strong> в переключателе режимов в левом нижнем углу главного окна (в первый раз, когда появится вопрос, где собеседник, оставьте <strong>На встрече</strong>). Затем в блоке <strong>Речь</strong> в настройках выключите <strong>Перевод, который слышит собеседник</strong> и оставьте выключенным <strong>Перевод, который слышу я</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте два языка',
      content:
        'Укажите в поле <strong>я говорю на</strong> свой язык, а в поле <strong>собеседник читает</strong> — язык собеседника. Строка под полями показывает обратное направление. Здесь: я говорю на китайском, собеседник читает на английском.',
      screenshot: '02-languages.png',
      tip: 'Не оставляйте в поле «я говорю на» значение «Автоопределение». В режиме «Оба» Sokuji должен знать ваш язык, чтобы переводить на него собеседника, и кнопка «Старт» остаётся недоступной, пока вы его не выберете.',
    },
    {
      title: 'Выберите микрофон и приложение для встреч',
      content:
        'В настройках, в разделе <strong>Микрофон</strong>, выберите свой настоящий микрофон. В разделе <strong>Аудио собеседника</strong> выберите приложение для встреч, здесь <strong>Microsoft Teams</strong>. В приложении для встреч вы тоже продолжаете использовать свой настоящий микрофон: Sokuji только слушает. В блоке <strong>Речь</strong> параметры <strong>Перевод, который слышит собеседник</strong> и <strong>Перевод, который слышу я</strong> оба выключены, поэтому ничего не озвучивается.',
      screenshot: '03-devices.png',
      tip: 'Если вашего приложения для встреч нет в списке, сначала подключитесь к звонку (приложение часто появляется в списке только после того, как начнёт воспроизводить звук) и нажмите значок обновления или выберите System Audio (All Applications).',
    },
    {
      title: 'Начните перевод',
      content:
        'Нажмите <strong>Старт</strong>. Sokuji показывает речь обеих сторон в виде субтитров, каждую строку с переводом. Чтобы собеседник тоже мог читать, покажите окно Sokuji или откройте окно субтитров кнопкой <strong>Субтитры</strong> в строке заголовка и покажите его.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Сохраните расшифровку',
      content:
        'Нажмите кнопку загрузки на панели инструментов. В разделе <strong>Включить</strong> выберите, сохранять ли для каждой стороны оригинал (<strong>Оригинал</strong>) и перевод (<strong>Перевод</strong>), затем нажмите <strong>Копировать в буфер обмена</strong>, <strong>Скачать как .txt</strong> или <strong>Скачать как .json</strong>. Если включено <strong>Автосохранение по окончании сессии</strong>, Sokuji сохраняет расшифровку за вас при каждой остановке. Параметры становятся доступны, когда есть что сохранить.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Советы для лучших результатов',
    items: [
      'Надевайте наушники. Иначе микрофон улавливает собеседника из динамиков, и Sokuji показывает его слова как ваши.',
      'Выбирайте приложение для встреч, а не System Audio, чтобы звуки уведомлений и музыка из других программ не переводились.',
      'Останавливайте сеанс, когда встреча заканчивается: запущенный сеанс продолжает расходовать ваш баланс.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Видит ли собеседник субтитры?',
        answer: 'Только если вы показываете свой экран или окно субтитров. Иначе субтитры видны только на вашем компьютере.',
      },
      {
        question: 'Нужен ли для этого VB-CABLE?',
        answer: 'Нет. Виртуальный аудиокабель нужен только тогда, когда собеседник должен слышать ваши слова переведённым голосом.',
      },
      {
        question: 'Можно ли позже перейти на переведённый голос?',
        answer: 'Да. Включите «Перевод, который слышит собеседник» и следуйте руководству по двусторонним встречам, чтобы настроить виртуальный аудиокабель.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Zweisprachige Untertitel für beide Seiten eines Gesprächs',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Diese Anleitung richtet die Sokuji-Desktop-App so ein, dass sie beide Seiten eines Gesprächs als übersetzten Text anzeigt: was Sie sagen und was Ihr Gegenüber sagt, jeweils mit Übersetzung. Nutzen Sie das für zweisprachige Untertitel, um einem Anruf zu folgen, in dem jeder seine eigene Sprache spricht, oder für ein übersetztes Transkript eines Meetings. Es wird keine synthetische Stimme erzeugt, nichts ins Meeting gesendet, und Sie brauchen kein virtuelles Audiokabel. Verwendet wird KizunaAI (Unterstützt von Soniox; die Qwen-Engine funktioniert genauso). Die Screenshots stammen aus Sokuji 0.44.0 unter Windows mit Microsoft Teams, zwischen Chinesisch und Englisch.',
  },
  steps: [
    {
      title: 'Sokuji mit KizunaAI einrichten',
      content:
        'Falls noch nicht geschehen, folgen Sie dem <a href="/docs/kizuna-ai/quick-start">KizunaAI-Schnellstart</a>, um Sokuji zu installieren und sich anzumelden.',
    },
    {
      title: '„Zweiseitiges Online-Gespräch, nur Untertitel“ wählen',
      content:
        'Wählen Sie in der Einrichtung <strong>Zweiseitiges Online-Gespräch, nur Untertitel</strong>. Damit wechselt Sokuji in den Modus <strong>Beides</strong>, nur mit Untertiteln. Wenn Sie die Einrichtung bereits abgeschlossen haben, klicken Sie im Modusschalter unten links im Hauptfenster auf <strong>Beides</strong> (behalten Sie beim ersten Mal <strong>In einem Meeting</strong> bei, wenn gefragt wird, wo das Gegenüber ist). Schalten Sie dann im Bereich <strong>Sprache</strong> der Einstellungen <strong>Übersetzung, die das Gegenüber hört</strong> aus und lassen Sie <strong>Übersetzung, die ich höre</strong> ausgeschaltet.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Die beiden Sprachen einstellen',
      content:
        'Stellen Sie bei <strong>Ich spreche</strong> Ihre eigene Sprache ein und bei <strong>sie lesen</strong> die Ihres Gegenübers. Die Zeile darunter zeigt die Gegenrichtung. Hier: Ich spreche Chinesisch, sie lesen Englisch.',
      screenshot: '02-languages.png',
      tip: 'Lassen Sie „Ich spreche“ nicht auf „Automatische Erkennung“. Im Modus „Beides“ muss Sokuji Ihre Sprache kennen, um die andere Seite in sie zu übersetzen, und „Starten“ bleibt deaktiviert, bis Sie eine auswählen.',
    },
    {
      title: 'Mikrofon und Meeting-App wählen',
      content:
        'Wählen Sie in den Einstellungen unter <strong>Mikrofon</strong> Ihr echtes Mikrofon. Wählen Sie unter <strong>Audio des Gegenübers</strong> Ihre Meeting-App, hier <strong>Microsoft Teams</strong>. In der Meeting-App verwenden Sie ebenfalls weiter Ihr echtes Mikrofon: Sokuji hört nur zu. Im Bereich <strong>Sprache</strong> sind <strong>Übersetzung, die das Gegenüber hört</strong> und <strong>Übersetzung, die ich höre</strong> beide ausgeschaltet, sodass nichts gesprochen wird.',
      screenshot: '03-devices.png',
      tip: 'Wenn Ihre Meeting-App nicht in der Liste steht, treten Sie zuerst dem Anruf bei (eine App erscheint oft erst, wenn sie Ton wiedergibt) und klicken Sie auf das Aktualisieren-Symbol, oder wählen Sie System Audio (All Applications).',
    },
    {
      title: 'Übersetzung starten',
      content:
        'Klicken Sie auf <strong>Starten</strong>. Sokuji zeigt beide Seiten als Untertitel an, jede Zeile mit ihrer Übersetzung. Damit Ihr Gegenüber mitlesen kann, geben Sie das Sokuji-Fenster frei oder öffnen Sie mit <strong>Untertitel</strong> in der Titelleiste das Untertitelfenster und geben dieses frei.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Das Transkript speichern',
      content:
        'Klicken Sie auf die Download-Schaltfläche in der Symbolleiste. Wählen Sie unter <strong>Einschließen</strong>, ob für jede Seite das Original (<strong>Original</strong>) und die Übersetzung (<strong>Übersetzung</strong>) enthalten sein sollen, und wählen Sie dann <strong>In Zwischenablage kopieren</strong>, <strong>Als .txt herunterladen</strong> oder <strong>Als .json herunterladen</strong>. Ist <strong>Bei Sitzungsende automatisch speichern</strong> eingeschaltet, speichert Sokuji es bei jedem Beenden für Sie. Die Optionen werden verfügbar, sobald es etwas zu speichern gibt.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Tipps für beste Ergebnisse',
    items: [
      'Tragen Sie Kopfhörer. Sonst nimmt Ihr Mikrofon Ihr Gegenüber aus den Lautsprechern auf, und Sokuji zeigt seine Worte als Ihre an.',
      'Wählen Sie die Meeting-App statt System Audio, damit Benachrichtigungstöne und Musik aus anderen Programmen nicht übersetzt werden.',
      'Beenden Sie die Sitzung, wenn das Meeting vorbei ist: Eine laufende Sitzung verbraucht weiter Ihr Guthaben.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Sieht mein Gegenüber die Untertitel?',
        answer: 'Nur wenn Sie Ihren Bildschirm oder das Untertitelfenster freigeben. Sonst erscheinen die Untertitel nur auf Ihrem Computer.',
      },
      {
        question: 'Brauche ich dafür VB-CABLE?',
        answer: 'Nein. Das virtuelle Audiokabel wird nur gebraucht, wenn Ihr Gegenüber Ihre Worte als übersetzte Stimme hören soll.',
      },
      {
        question: 'Kann ich später zu einer übersetzten Stimme wechseln?',
        answer: 'Ja. Schalten Sie „Übersetzung, die das Gegenüber hört“ ein und folgen Sie der Anleitung für zweiseitige Meetings, um das virtuelle Audiokabel einzurichten.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Двомовні субтитри для обох сторін розмови',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Цей посібник налаштовує настільний застосунок Sokuji так, щоб обидві сторони розмови відображалися у вигляді перекладеного тексту: ваші слова й слова співрозмовника, кожні зі своїм перекладом. Використовуйте це для двомовних субтитрів, щоб стежити за дзвінком, де кожен говорить своєю мовою, або щоб отримати перекладену розшифровку зустрічі. Синтезований голос не створюється, на зустріч нічого не надсилається, і віртуальний аудіокабель не потрібен. Використовується KizunaAI (на базі Soniox; рушій Qwen працює так само). Знімки екрана зроблено в Sokuji 0.44.0 на Windows з Microsoft Teams, між китайською та англійською.',
  },
  steps: [
    {
      title: 'Налаштуйте Sokuji з KizunaAI',
      content:
        'Якщо ви ще цього не зробили, дотримуйтеся посібника <a href="/docs/kizuna-ai/quick-start">«Швидкий старт із KizunaAI»</a>, щоб установити Sokuji та увійти в обліковий запис.',
    },
    {
      title: 'Виберіть «Двостороння онлайн-розмова, лише субтитри»',
      content:
        'У майстрі налаштування виберіть <strong>Двостороння онлайн-розмова, лише субтитри</strong>. Sokuji переходить у режим <strong>Обидва</strong> лише із субтитрами. Якщо ви вже завершили налаштування, натисніть <strong>Обидва</strong> в перемикачі режимів у лівому нижньому куті головного вікна (першого разу, коли панель запитає, де співрозмовник, залиште <strong>На зустрічі</strong>). Потім у блоці <strong>Мовлення</strong> налаштувань вимкніть <strong>Переклад, який чує співрозмовник</strong> і залиште <strong>Переклад, який чую я</strong> вимкненим.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте дві мови',
      content:
        'Укажіть у полі <strong>Я говорю</strong> свою мову, а в полі <strong>вони читають</strong> — мову співрозмовника. Рядок під полями показує зворотний напрямок. Тут: Я говорю китайською, вони читають англійською.',
      screenshot: '02-languages.png',
      tip: 'Не залишайте в полі «Я говорю» значення «Автовиявлення». У режимі «Обидва» Sokuji має знати вашу мову, щоб перекладати нею співрозмовника, і кнопка «Почати» залишається недоступною, доки ви її не виберете.',
    },
    {
      title: 'Виберіть мікрофон і застосунок для зустрічей',
      content:
        'У налаштуваннях, у розділі <strong>Мікрофон</strong>, виберіть свій справжній мікрофон. У розділі <strong>Аудіо співрозмовника</strong> виберіть застосунок для зустрічей, тут <strong>Microsoft Teams</strong>. У застосунку для зустрічей ви теж і далі використовуєте свій справжній мікрофон: Sokuji лише слухає. У блоці <strong>Мовлення</strong> <strong>Переклад, який чує співрозмовник</strong> і <strong>Переклад, який чую я</strong> вимкнено, тож нічого не озвучується.',
      screenshot: '03-devices.png',
      tip: 'Якщо вашого застосунку для зустрічей немає в списку, спочатку приєднайтеся до дзвінка (застосунок часто з’являється в списку лише тоді, коли починає відтворювати звук) і натисніть значок оновлення або виберіть System Audio (All Applications).',
    },
    {
      title: 'Почніть переклад',
      content:
        'Натисніть <strong>Почати</strong>. Sokuji показує мовлення обох сторін у вигляді субтитрів, кожен рядок із перекладом. Щоб співрозмовник теж міг читати, покажіть вікно Sokuji або відкрийте вікно субтитрів кнопкою <strong>Субтитри</strong> в рядку заголовка й покажіть його.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Збережіть розшифровку',
      content:
        'Натисніть кнопку завантаження на панелі інструментів. У розділі <strong>Включити</strong> виберіть, чи зберігати для кожної сторони оригінал (<strong>Оригінал</strong>) і переклад (<strong>Переклад</strong>), потім натисніть <strong>Копіювати в буфер обміну</strong>, <strong>Завантажити як .txt</strong> або <strong>Завантажити як .json</strong>. Якщо ввімкнено <strong>Автозбереження після завершення сесії</strong>, Sokuji зберігає розшифровку за вас після кожної зупинки. Параметри стають доступними, коли є що зберегти.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Поради для найкращих результатів',
    items: [
      'Надягайте навушники. Інакше мікрофон уловлює співрозмовника з динаміків, і Sokuji показує його слова як ваші.',
      'Вибирайте застосунок для зустрічей, а не System Audio, щоб звуки сповіщень і музика з інших програм не перекладалися.',
      'Зупиняйте сеанс, коли зустріч закінчується: запущений сеанс і далі витрачає ваш баланс.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чи бачить співрозмовник субтитри?',
        answer: 'Лише якщо ви показуєте свій екран або вікно субтитрів. Інакше субтитри видно лише на вашому комп’ютері.',
      },
      {
        question: 'Чи потрібен для цього VB-CABLE?',
        answer: 'Ні. Віртуальний аудіокабель потрібен лише тоді, коли співрозмовник має чути ваші слова перекладеним голосом.',
      },
      {
        question: 'Чи можна пізніше перейти на перекладений голос?',
        answer: 'Так. Увімкніть «Переклад, який чує співрозмовник» і дотримуйтеся посібника з двосторонніх зустрічей, щоб налаштувати віртуальний аудіокабель.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Sottotitoli bilingui per entrambe le parti di una conversazione',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Questa guida configura l’app desktop di Sokuji per mostrare entrambe le parti di una conversazione come testo tradotto: ciò che dici tu e ciò che dice l’altra persona, ciascuno con la sua traduzione. Usala per sottotitoli bilingui, per seguire una chiamata in cui ognuno parla la propria lingua o per avere una trascrizione tradotta di una riunione. Non viene generata alcuna voce sintetica, nella riunione non viene inviato nulla, e non ti serve un cavo audio virtuale. Usa KizunaAI (Con tecnologia Soniox; il motore Qwen funziona allo stesso modo). Le schermate provengono da Sokuji 0.44.0 su Windows con Microsoft Teams, tra cinese e inglese.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Se non l’hai ancora fatto, segui la <a href="/docs/kizuna-ai/quick-start">guida rapida a KizunaAI</a> per installare Sokuji e accedere.',
    },
    {
      title: 'Scegli “Conversazione online bidirezionale, solo sottotitoli”',
      content:
        'Nella configurazione guidata, scegli <strong>Conversazione online bidirezionale, solo sottotitoli</strong>. Sokuji passa in modalità <strong>Entrambi</strong>, solo con i sottotitoli. Se hai già completato la configurazione, fai clic su <strong>Entrambi</strong> nel selettore di modalità in basso a sinistra nella finestra principale (la prima volta, quando ti chiede dov’è l’altra parte, lascia <strong>In una riunione</strong>). Poi, nella sezione <strong>Voce</strong> delle Impostazioni, disattiva <strong>Traduzione che sente l\'altra parte</strong> e lascia disattivata <strong>Traduzione che sento io</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Imposta le due lingue',
      content:
        'Imposta <strong>Io parlo</strong> sulla tua lingua e <strong>loro leggono</strong> su quella dell’altra persona. La riga sottostante mostra l’altra direzione. Qui: Io parlo cinese, loro leggono inglese.',
      screenshot: '02-languages.png',
      tip: 'Non lasciare Io parlo su Rilevamento Automatico. In modalità Entrambi, Sokuji deve conoscere la tua lingua per tradurvi l’altra parte, e Avvia resta disattivato finché non ne scegli una.',
    },
    {
      title: 'Scegli il microfono e l’app per riunioni',
      content:
        'In Impostazioni, in <strong>Microfono</strong>, scegli il tuo microfono reale. In <strong>Audio dell\'altro</strong>, scegli la tua app per riunioni, qui <strong>Microsoft Teams</strong>. Continui a usare il tuo microfono reale anche nell’app per riunioni: Sokuji ascolta soltanto. Nella sezione <strong>Voce</strong>, <strong>Traduzione che sente l\'altra parte</strong> e <strong>Traduzione che sento io</strong> sono entrambe disattivate, quindi nulla viene pronunciato.',
      screenshot: '03-devices.png',
      tip: 'Se la tua app per riunioni non è nell’elenco, entra prima nella chiamata (spesso un’app compare solo quando riproduce audio) e fai clic sull’icona di aggiornamento, oppure scegli System Audio (All Applications).',
    },
    {
      title: 'Inizia a tradurre',
      content:
        'Fai clic su <strong>Avvia</strong>. Sokuji mostra entrambe le parti come sottotitoli, ogni riga con la sua traduzione. Per far leggere anche l’altra persona, condividi la finestra di Sokuji oppure apri la finestra dei sottotitoli con <strong>Sottotitoli</strong> nella barra del titolo e condividi quella.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Salva la trascrizione',
      content:
        'Fai clic sul pulsante di download nella barra degli strumenti. In <strong>Includi</strong>, scegli se conservare l’originale (<strong>Originale</strong>) e la traduzione (<strong>Traduzione</strong>) di ciascuna parte, poi scegli <strong>Copia negli appunti</strong>, <strong>Scarica come .txt</strong> o <strong>Scarica come .json</strong>. Con <strong>Salva automaticamente a fine sessione</strong> attivo, Sokuji la salva per te ogni volta che ti fermi. Le opzioni diventano disponibili quando c’è qualcosa da salvare.',
      screenshot: '05-export-menu.png',
    },
  ],
  tips: {
    title: 'Consigli per risultati migliori',
    items: [
      'Indossa le cuffie. Altrimenti il microfono capta l’altra persona dai tuoi altoparlanti, e Sokuji mostra le sue parole come se fossero tue.',
      'Scegli l’app per riunioni invece di System Audio, così i suoni delle notifiche e la musica di altri programmi non vengono tradotti.',
      'Interrompi la sessione quando la riunione finisce: una sessione attiva continua a consumare il tuo saldo.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'L’altra persona vede i sottotitoli?',
        answer: 'Solo se condividi lo schermo o la finestra dei sottotitoli. Altrimenti i sottotitoli compaiono solo sul tuo computer.',
      },
      {
        question: 'Mi serve VB-CABLE?',
        answer: 'No. Il cavo audio virtuale serve solo quando l’altra persona deve sentire le tue parole come voce tradotta.',
      },
      {
        question: 'Posso passare a una voce tradotta in seguito?',
        answer: 'Sì. Attiva Traduzione che sente l\'altra parte e segui la guida alle riunioni bidirezionali per configurare il cavo audio virtuale.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function TwoWaySubtitlesTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/two-way-subtitles" />;
}
